import { Box, Flex, useMantineTheme } from '@mantine/core';
import type * as Schemas from '@repo/api-codegen';
import { Text } from '@repo/foundations/components/Text/Text';
import { useCallback, useMemo } from 'react';
import { BaseOverviewChart } from '../../../components/Overview/BaseOverviewChart';
import { DATE_FILTER_OPTIONS } from '../constants';

export function AiChatOverviewChart({
	timeseries,
	dateFilter,
	setDateFilter,
	setTimeRangeValue,
	timeRangeValue,
	dateRangeOption = 'day',
	onClick,
}: {
	timeseries: Schemas.AiChatTimeSeries;
	dateFilter: string;
	setDateFilter: (dateFilter: string) => void;
	setTimeRangeValue: (timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	}) => void;
	timeRangeValue: {
		timeRange: string;
		dateRange: { from: Date | null; to: Date | null };
	};
	dateRangeOption?: 'day' | 'week' | 'month' | 'year';
	onClick?: (dataIndex: number) => void;
}) {
	const theme = useMantineTheme();

	const channels = useMemo(
		() => [
			{
				label: 'Total AI Chats',
				color: theme.other.getColor('icon/chart/1'),
				x: 'date',
				y: 'total_chats',
				renderArea: true,
				areaOpacity: 0.4,
				lineWidth: 2,
				zIndex: 1,
			},
		],
		[theme]
	);

	const tooltipDataRenderer = useCallback(
		(dataIndex: number) => {
			const point = timeseries.points[dataIndex];
			if (!point) return null;

			return (
				<Box p="sm">
					<Text mb="sm" size="sm" color="text/secondary/default">
						{new Date(point.timestamp).toLocaleDateString([], {
							month: 'long',
							day: 'numeric',
							year: 'numeric',
						})}
					</Text>
					<Flex direction="column" gap="xs" mt="sm">
						<Flex justify="space-between" gap="sm">
							<Flex gap="xs" align="center">
								<Box
									w={16}
									h={2}
									bg={channels[0].color}
									style={{
										borderRadius: theme.radius.md,
									}}
									aria-hidden="true"
								/>
								<Text size="sm">AI chats created</Text>
							</Flex>
							<Text fw={500} size="sm">
								{point.total_chats}
							</Text>
						</Flex>
					</Flex>
				</Box>
			);
		},
		[timeseries.points, channels, theme]
	);

	const yAxisFormatter = (value: number) => value.toString();

	return (
		<BaseOverviewChart
			timeseries={{
				points: timeseries.points.map((point) => ({
					timestamp: point.timestamp,
					total_chats: point.total_chats,
				})),
				granularity: timeseries.granularity,
				available_channels: timeseries.available_channels,
				available_dimensions: [],
			}}
			dateFilter={dateFilter}
			setDateFilter={setDateFilter}
			setTimeRangeValue={setTimeRangeValue}
			timeRangeValue={timeRangeValue}
			dateRangeOption={dateRangeOption}
			setDateRangeOption={() => {}}
			title="AI chats"
			dateFilterOptions={DATE_FILTER_OPTIONS}
			channels={channels}
			tooltipDataRenderer={tooltipDataRenderer}
			analyticsEventPrefix="ai_chat_usage/overview"
			yAxisFormatter={yAxisFormatter}
			hideDateFilter={true}
			hideDateRangeButtons={true}
			onClick={onClick}
		/>
	);
}
